import { createRouter, createWebHistory } from "vue-router";
import { shopInfoById } from "@/api/shops";
import { useShopStore } from "@/store";
import {
    prefetchRouteWithDeps,
    smartPrefetch,
    prefetchRoute,
    prefetchRouteResources,
    prefetchRouteByPath
} from "@/utils/routePrefetch";

// 定义路由组件加载器
const routeLoaders = {
    Dashboard: () => import("@/views/Dashboard/index.vue"),
    Reports: () => import("@/views/Reports/index.vue"),

    // -------------- 订阅模块路由 start  ---------
    Subscriptions: () => import("@/views/Subscriptions/index.vue"),
    SubscriptionsReports: () => import("@/views/Subscriptions/reports.vue"),
    // SubscriptionsAnalysis: () => import("@/views/Subscriptions/analytics.vue"),
    SubscriptionsRules: () => import("@/views/Subscriptions/rules.vue"),
    SubscriptionsList: () => import("@/views/Subscriptions/list.vue"),
    SubscriptionsOrderList: () => import("@/views/Subscriptions/orderList.vue"),
    // -------------- 订阅模块路由 end  ---------

    Zones: () => import("@/views/Zones/index.vue"),
    Funnels: () => import("@/views/Funnels/index.vue"),
    FunnelDetail: () => import("@/views/Funnels/page/steps.vue"),
    FunnelCheckoutLayoutView: () => import("@/views/Funnels/page/checkoutLayoutView.vue"),
    FunnelUpsellView: () => import("@/views/Funnels/page/upsellView.vue"),
    UpsellPage: () => import("@/views/UpsellPage/index.vue"),
    Helps: () => import("@/views/Helps/index.vue"),

    // -------------- 设置模块路由 start  ---------
    Settings: () => import("@/views/Settings/index.vue"),
    AccountSettings: () => import("@/views/Settings/account.vue"),
    PaymentSettings: () => import("@/views/Settings/payment.vue"),
    IntegrationsSettings: () => import("@/views/Settings/integrations.vue"),
    GeneralSettings: () => import("@/views/Settings/settings.vue"),
    SellingPlanSettings: () => import("@/views/Settings/sellingPlan.vue"),
    // PolicyLinkSettings: () => import("@/views/Settings/policyLink.vue"),
    // PolicyManagementSettings: () => import("@/views/Settings/policyManagement.vue"),
    // -------------- 设置模块路由 start  ---------
};

const routes = [
    {
        path: "/dashboard",
        name: "Dashboard",
        component: routeLoaders.Dashboard,
    },
    {
        path: "/reports",
        name: "Reports",
        component: routeLoaders.Reports,
    },
    {
        path: "/subscriptions",
        name: "Subscriptions",
        component: routeLoaders.Subscriptions,
        children: [
            {
                path: "reports",
                name: "SubscriptionsReports",
                component: routeLoaders.SubscriptionsReports,
            },
            // {
            //     path: "analytics",
            //     name: "SubscriptionsAnalysis",
            //     component: routeLoaders.SubscriptionsAnalysis,
            // },
            {
                path: "rules",
                name: "SubscriptionsRules",
                component: routeLoaders.SubscriptionsRules,
            },
            {
                path: "list",
                name: "SubscriptionsList",
                component: routeLoaders.SubscriptionsList,
            },
            {
                path: "orderList",
                name: "SubscriptionsOrderList",
                component: routeLoaders.SubscriptionsOrderList,
            },
        ]
    },
    {
        path: "/zones",
        name: "Zones",
        component: routeLoaders.Zones,
    },
    {
        path: "/funnels",
        name: "Funnels",
        component: routeLoaders.Funnels,
    },
    {
        path: "/funnel-view/:id",
        name: "FunnelDetail",
        component: routeLoaders.FunnelDetail,
        meta: { hideAppHeader: true, fullBleed: true },
    },
    {
        path: "/funnel-checkout-layout-view/:id",
        name: "FunnelCheckoutLayoutView",
        component: routeLoaders.FunnelCheckoutLayoutView,
        meta: { hideAppHeader: true, fullBleed: true },
    },
    {
        path: "/funnel-upsell-view/:id/:upsellId",
        name: "FunnelUpsellView",
        component: routeLoaders.FunnelUpsellView,
    },
    {
        path: "/upsell-page/:id/:upsellId",
        name: "UpsellPage",
        component: routeLoaders.UpsellPage,
    },
    {
        path: "/helps",
        name: "Helps",
        component: routeLoaders.Helps,
    },
    {
        path: "/settings",
        name: "Settings",
        component: routeLoaders.Settings,
        children: [
            {
                path: "account",
                name: "AccountSettings",
                component: routeLoaders.AccountSettings,
            },
            {
                path: "payment",
                name: "PaymentSettings",
                component: routeLoaders.PaymentSettings,
            },
            {
                path: "integrations",
                name: "IntegrationsSettings",
                component: routeLoaders.IntegrationsSettings,
            },
            {
                path: "settings",
                name: "GeneralSettings",
                component: routeLoaders.GeneralSettings,
            },
            // {
            //     path: "selling-plan",
            //     name: "SellingPlanSettings",
            //     component: routeLoaders.SellingPlanSettings,
            // },
            // {
            //     path: "policy-link",
            //     name: "PolicyLinkSettings",
            //     component: routeLoaders.PolicyLinkSettings,
            // },
            // {
            //     path: "policy-management",
            //     name: "PolicyManagementSettings",
            //     component: routeLoaders.PolicyManagementSettings,
            // },
            {
                path: "",
                name: "SettingsDefault",
                redirect: "/settings/account",
            },
        ],
    },
    {
        path: "/",
        redirect: "/dashboard",
    },
];

// 注册所有路由组件到预加载映射（但不立即预加载）
Object.entries(routeLoaders).forEach(([name, loader]) => {
    prefetchRouteWithDeps(name, loader);
});

// 在应用启动后预加载主要路由（延迟执行，避免阻塞初始加载）
if (typeof window !== 'undefined') {
    // 使用 requestIdleCallback 在浏览器空闲时预加载主要路由
    const prefetchMainRoutes = () => {
        const mainRoutes = ['Dashboard', 'Reports', 'Funnels', 'Zones'];
        mainRoutes.forEach((name) => {
            const loader = routeLoaders[name as keyof typeof routeLoaders];
            if (loader) {
                // 使用低优先级预加载，避免影响初始页面加载
                prefetchRoute(name, loader, 'low');
            }
        });
    };

    // 延迟预加载，等待页面完全加载后再开始
    if (document.readyState === 'complete') {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(prefetchMainRoutes, { timeout: 2000 });
        } else {
            setTimeout(prefetchMainRoutes, 1000);
        }
    } else {
        window.addEventListener('load', () => {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(prefetchMainRoutes, { timeout: 2000 });
            } else {
                setTimeout(prefetchMainRoutes, 1000);
            }
        });
    }
}

const router = createRouter({
    // 生产 Pages 部署时 base=/app/，SSH 部署时为 /；与 vite base 保持一致
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

// 添加全局路由守卫
router.beforeEach(async (to, from, next) => {
    // 本地开发环境跳过鉴权
    // const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV;
    // if (isDevelopment) {
    //   next();
    //   return;
    // }

    // 如果目标路由与当前路由相同，且查询参数也相同，则允许导航
    if (
        to.path === from.path &&
        JSON.stringify(to.query) === JSON.stringify(from.query)
    ) {
        next();
        return;
    }

    // 预加载目标路由资源（在路由切换前就开始预加载）
    if (to.name && typeof to.name === 'string') {
        const targetLoader = routeLoaders[to.name as keyof typeof routeLoaders];
        if (targetLoader) {
            // 立即预加载目标路由，使用高优先级
            prefetchRouteResources(to.name, targetLoader);
        } else {
            // 如果没有路由名称，尝试通过路径预加载
            prefetchRouteByPath(to.path);
        }
    } else {
        // 通过路径预加载
        prefetchRouteByPath(to.path);
    }

    // 智能预加载：在路由切换时预加载可能访问的下一个路由（后台进行）
    if (from.name && typeof from.name === 'string') {
        smartPrefetch(from.name);
    }

    try {
        const shopId = (to.query.shop || "").toString().split(".")[0] as string;
        const shopStore = useShopStore();
        // 检查是否已经安装过
        if (!shopStore.shopState && shopId) {
            shopStore.setShopState(false, 'loading...');
            const shopInfo = await shopInfoById(shopId);
            // 将获取到的店铺信息写入 store，避免页面再次重复拉取
            try {
                shopStore.setShopId(shopId);
                // 直接写入 /shopify-app/shop 的 data，由 store 侧归一化
                shopStore.setShopDetail(shopInfo as any);
            } catch {
            }
            // TODO: 暂时把shopInfo.appStatus == -1 改成 1
            if (shopInfo && !shopInfo.installed) {
                shopStore.setShopState(false, 'installing...');

                const scope = [
                    "read_orders",
                    "write_orders",
                    "write_draft_orders",
                    "read_products",
                    "write_products",
                    "read_customers",
                    "read_inventory",
                    "write_discounts",
                    "read_discounts",
                    "write_marketing_events",
                    "read_marketing_events",
                    "write_products",
                    "read_products",
                    "read_orders",
                    "write_themes",
                    "read_themes",
                    "write_content",
                    "read_content",
                    "write_online_store_pages",
                    "read_online_store_pages",
                    "read_analytics",
                    "read_reports",
                    "read_customers",
                    "read_price_rules",
                    "read_legal_policies",
                    "read_publications"
                ];
                try {
                    console.log('开始安装')
                    // const res = await shopInstall(shopId, { scope: scope.join(",") });
                    window.location.href = `${import.meta.env.VITE_API_BASE_URL
                        }/shopify-app/shop/install?shopifyId=${shopId}&scope=${scope.join(",")}`;
                } catch (error) {
                    console.error("Error in route guard:", error);
                }
                // 阻止路由继续导航
                next(false);
                return;
            } else {
                shopStore.setShopState(true);
                next();
                return;
            }
        }
        //await shopInstall(to.query.shop as string, { scope: "read_analytics,read_apps" });

        // 如果目标路由已经包含了 shop 参数，直接导航
        if (shopId) {
            next();
            return;
        }

        // 如果源路由有 shop 参数，但目标路由没有，则添加 shop 参数
        if (from.query.shop && !to.query.shop) {
            next({
                path: to.path,
                query: { ...to.query, shop: from.query.shop },
            });
            return;
        }

        // 其他情况直接导航
        next();
    } catch (error) {
        console.error("Error in route guard:", error);
        // 发生错误时也继续导航，避免页面卡住
        next();
    }
});

export default router;
